import React, { useEffect, useState } from "react";
import { fetchTemplates, createTemplate, deleteTemplate } from "../../services/adminTemplateService";
import { fetchActivePlans } from "../../services/planService";
import { fetchActiveSpeakers } from "../../services/adminSpeakerServices";

const LEVELS  = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const autoEnd = (start) => {
  if (!start) return "";
  const [h, m] = start.split(":").map(Number);
  const e = new Date(0, 0, 0, h, m + 30);
  return `${String(e.getHours()).padStart(2, "0")}:${String(e.getMinutes()).padStart(2, "0")}`;
};
const blankRow = (prev = null) => ({
  startTime: prev ? prev.endTime : "", endTime: prev ? autoEnd(prev.endTime) : "",
  level: prev?.level || "", planId: prev?.planId || "",
  speakerId: prev?.speakerId || "", totalSlots: prev?.totalSlots || 10, sortOrder: 0,
});

const TemplateManagerModal = ({ onClose, days = [] }) => {
  const [tab, setTab]             = useState("list");
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans]         = useState([]);
  const [speakers, setSpeakers]   = useState([]);
  const [preview, setPreview]     = useState(null);
  const [name, setName]           = useState("");
  const [rows, setRows]           = useState([blankRow()]);
  const [sourceDay, setSourceDay] = useState("");
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    load();
    fetchActivePlans().then((r)    => setPlans(r.data || []));
    fetchActiveSpeakers().then((r) => setSpeakers(r.data || []));
  }, []);

  const load = async () => {
    const r = await fetchTemplates();
    if (r.status === 200) setTemplates(r.data || []);
  };

  const handleSourceDay = (dayId) => {
    setSourceDay(dayId);
    const day = days.find((d) => String(d.id) === String(dayId));
    if (!day?.sessions?.length) return;
    const toHHmm = (utc) => {
      if (!utc) return "";
      const d = new Date(utc);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    };
    setRows([...day.sessions]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map((s, i) => ({ startTime: toHHmm(s.startTime), endTime: toHHmm(s.endTime), level: s.level || "", planId: s.planId || "", speakerId: s.speakerId || "", totalSlots: s.totalSlots || 10, sortOrder: i }))
    );
  };

  const updateRow = (idx, field, val) =>
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const handleSave = async () => {
    if (!name.trim()) return alert("Template name is required.");
    setSaving(true);
    const res = await createTemplate({
      name: name.trim(),
      sessions: rows.map((r, i) => ({
        startTime: r.startTime, endTime: r.endTime,
        level: r.level || null, planId: r.planId ? Number(r.planId) : null,
        speakerId: r.speakerId ? Number(r.speakerId) : null,
        totalSlots: Number(r.totalSlots) || 1, sortOrder: i,
      })),
    });
    setSaving(false);
    if (res.status === 200 || res.status === 201) {
      setName(""); setRows([blankRow()]); setSourceDay(""); setTab("list"); load();
    } else {
      alert(res.message || "Failed to save template.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await deleteTemplate(id);
    load();
  };

  const planName    = (id) => plans.find((p) => p.id === id)?.planName || "—";
  const speakerName = (id) => speakers.find((s) => s.id === id)?.name  || "—";

  return (
    <div className="tt-modal-overlay" onClick={onClose}>
      <div className="tt-modal" style={{ width: "min(760px, 95vw)" }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="tt-modal-header">
          <h3>⊞ Session Templates</h3>
          <button className="tt-drawer-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="tt-tabs" style={{ padding: "0 22px", marginBottom: 0 }}>
          <button className={`tt-tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>My Templates</button>
          <button className={`tt-tab ${tab === "create" ? "active" : ""}`} onClick={() => setTab("create")}>+ Create Template</button>
        </div>

        {/* Body */}
        <div className="tt-modal-body">

          {/* LIST */}
          {tab === "list" && (
            templates.length === 0
              ? <div className="adm-empty" style={{ padding: "40px 0" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <p style={{ margin: 0, fontWeight: 600 }}>No templates yet</p>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>Create one to speed up scheduling.</p>
                </div>
              : templates.map((tpl) => (
                <div key={tpl.id} className="tt-tpl-card">
                  <div className="tt-tpl-card-header">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span className="tt-tpl-name">{tpl.name}</span>
                      <span className="tt-tpl-meta">{tpl.sessions?.length || 0} sessions</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="adm-btn adm-btn-ghost adm-btn-sm"
                        onClick={() => setPreview(preview === tpl.id ? null : tpl.id)}>
                        {preview === tpl.id ? "Hide" : "Preview"}
                      </button>
                      <button className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(tpl.id)}>Delete</button>
                    </div>
                  </div>
                  {preview === tpl.id && (
                    <div className="tt-session-table-wrap" style={{ borderRadius: 0, border: "none", borderTop: "1px solid #f1f5f9" }}>
                      <table className="tt-session-table">
                        <thead>
                          <tr>
                            {["Start", "End", "Level", "Plan", "Speaker", "Slots"].map((h) => <th key={h}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {[...(tpl.sessions || [])].sort((a, b) => a.sortOrder - b.sortOrder).map((s, i) => (
                            <tr key={i}>
                              <td>{s.startTime}</td>
                              <td>{s.endTime}</td>
                              <td>{s.level || "—"}</td>
                              <td>{planName(s.planId)}</td>
                              <td>{speakerName(s.speakerId)}</td>
                              <td>{s.totalSlots}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
          )}

          {/* CREATE */}
          {tab === "create" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div className="tt-field">
                  <label>Template Name *</label>
                  <input placeholder="e.g. Standard Monday" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                {days.length > 0 && (
                  <div className="tt-field">
                    <label>Pre-fill from existing day <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                    <select value={sourceDay} onChange={(e) => handleSourceDay(e.target.value)}>
                      <option value="">— Select a day —</option>
                      {days.map((d) => <option key={d.id} value={d.id}>{d.localDate} ({d.sessions?.length || 0} sessions)</option>)}
                    </select>
                  </div>
                )}
              </div>

              <p className="tt-section-label">Sessions</p>
              <div className="tt-rows-wrap">
                <table className="tt-rows-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}>#</th>
                      <th>Start</th><th>End</th><th>Level</th>
                      <th>Plan</th><th>Speaker</th>
                      <th style={{ width: 64 }}>Slots</th>
                      <th style={{ width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="tt-row-num">{idx + 1}</td>
                        <td><input type="time" value={row.startTime} onChange={(e) => { updateRow(idx, "startTime", e.target.value); updateRow(idx, "endTime", autoEnd(e.target.value)); }} /></td>
                        <td><input type="time" value={row.endTime} onChange={(e) => updateRow(idx, "endTime", e.target.value)} /></td>
                        <td>
                          <select value={row.level} onChange={(e) => updateRow(idx, "level", e.target.value)}>
                            <option value="">—</option>
                            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </td>
                        <td>
                          <select value={row.planId} onChange={(e) => updateRow(idx, "planId", e.target.value)}>
                            <option value="">—</option>
                            {plans.map((p) => <option key={p.id} value={p.id}>{p.planName}</option>)}
                          </select>
                        </td>
                        <td>
                          <select value={row.speakerId} onChange={(e) => updateRow(idx, "speakerId", e.target.value)}>
                            <option value="">—</option>
                            {speakers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </td>
                        <td><input type="number" min={1} value={row.totalSlots} onChange={(e) => updateRow(idx, "totalSlots", e.target.value)} /></td>
                        <td style={{ textAlign: "center" }}>
                          <button className="tt-icon-btn danger" onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}
                            disabled={rows.length === 1} style={{ width: 24, height: 24, fontSize: 12 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="tt-add-row-btn" onClick={() => setRows((p) => [...p, blankRow(p[p.length - 1])])}>
                + Add Row
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === "create" && (
          <div className="tt-modal-footer">
            <button className="adm-btn adm-btn-ghost" onClick={() => setTab("list")}>Cancel</button>
            <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Template"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManagerModal;
